import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

/**
 * Account status — the *exact* enum the Hub's CHECK constraint allows.
 *
 * Source of truth: `hub/app/store/models.py`:
 *
 *   CheckConstraint(
 *       "status IN ('connected', 'connecting', 'qr_required', 'down', 'banned')",
 *       name="ck_accounts_status",
 *   )
 *
 * Mirror it as a TS literal union so the StatusDot mapping table is exhaustive
 * and a new backend state is a compile-time error in the renderer (with a
 * defensive fallback rendered at runtime for forward-compat).
 *
 * `banned` (migration 0007) means the gateway reported a 403 ban signature
 * for the number that PERSISTED across two consecutive status polls; the hub
 * then auto-stems it, and a re-scan will NOT recover the number while the ban
 * lasts (retire / appeal). The hub holds the row `banned` until the gateway
 * reports it `connected` again or an admin re-scans it (the re-scan flips it
 * to `qr_required`) — `docs/contracts/hub-frontend-api.md` "Account health".
 */
export const ACCOUNT_STATUSES = [
  'connected',
  'connecting',
  'qr_required',
  'down',
  'banned',
] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

/**
 * `AccountAttention` — the hub's verdict that an account needs a human
 * (2026-09 disconnect early warning; evaluation rules in spec 01 §3.1, wire
 * shape in 00 §3.2, audited byte-for-byte per 01 §4):
 *
 *   { level:  "warning" | "critical",
 *     reason: "gateway_link_lost" | "restart_breaker" | "connecting_stuck" |
 *             "session_lost" | "qr_required" | "banned" | "down",
 *     scope:  "account" | "fleet",
 *     since:  datetime | null }
 *
 * `scope: "fleet"` marks a condition of the whole gateway link, not of the
 * one number (`gateway_link_lost`, `restart_breaker`) — the console shows it
 * once, as a fleet banner. `since` is the account's `status_since` (when the
 * current state began).
 *
 * The three literal sets are exported for exhaustive label / priority maps,
 * but the schema parses them as plain strings — the same forward-compat call
 * as `AccountOut.status`: this object rides on every row of
 * `GET /accounts`, so a reason a newer hub adds must degrade to a generic
 * warning, never fail the whole list's parse (the rollout recreates the hub
 * before the frontend).
 */
export const ACCOUNT_ATTENTION_LEVELS = ['warning', 'critical'] as const;
export type AccountAttentionLevel = (typeof ACCOUNT_ATTENTION_LEVELS)[number];

export const ACCOUNT_ATTENTION_REASONS = [
  'gateway_link_lost',
  'restart_breaker',
  'connecting_stuck',
  'session_lost',
  'qr_required',
  'banned',
  'down',
] as const;
export type AccountAttentionReason = (typeof ACCOUNT_ATTENTION_REASONS)[number];

export const ACCOUNT_ATTENTION_SCOPES = ['account', 'fleet'] as const;
export type AccountAttentionScope = (typeof ACCOUNT_ATTENTION_SCOPES)[number];

export const accountAttentionSchema = z.object({
  level: z.string(),
  reason: z.string(),
  scope: z.string(),
  since: z.string().nullable(),
});

export type AccountAttention = z.infer<typeof accountAttentionSchema>;

/**
 * Mirror of the Hub's `AccountOut` pydantic model (see
 * `hub/app/api/schemas.py`). `status` is loosened to `z.string()` so an
 * unknown future state from the Hub doesn't blow up the whole list — the
 * StatusDot's fallback branch handles it gracefully.
 */
const accountOutSchema = z.object({
  id: z.string().uuid(),
  platform: z.string(),
  external_account_id: z.string(),
  /** E.164 number of the paired phone (e.g. "+60165552121"), captured by the
   *  Hub at pairing time. `null` until the account has paired at least once
   *  (freshly-created rows carry an auto-generated `external_account_id` and
   *  no phone yet). */
  phone_e164: z.string().nullable(),
  status: z.string(),
  is_test: z.boolean(),
  /** 2026-09 early warning (spec 00 §3.2 `AccountOut` delta; hub migration
   *  0015):
   *    - `status_detail` — the gateway's last status detail for this
   *      account (≤300 chars, e.g. "Non-Error rejection"); `null` = none.
   *    - `status_since` — when the CURRENT `status` began (a detail-only
   *      change keeps it).
   *    - `attention` — the hub's verdict ({@link AccountAttention}); `null`
   *      = nothing needs a human.
   *  The WS `account.status` event patches all three live
   *  (`lib/realtime/projection.ts`). `.nullable().optional()`: pre-0015 hubs
   *  and locally-built rows omit them. */
  status_detail: z.string().nullable().optional(),
  status_since: z.string().nullable().optional(),
  attention: accountAttentionSchema.nullable().optional(),
  /** When the account was HIDDEN from the account pool (hub migration 0016:
   *  admin 「从账号池隐藏」 or the hub's auto-supersede once a re-paired row of
   *  the same number connects); `null` = visible. The hub still LISTS
   *  archived rows — old conversations are labelled with them — so every
   *  pool-like consumer filters through {@link visibleAccounts} while label
   *  lookups keep using the whole list. `.optional()`: a pre-0016 hub omits
   *  it (= visible). */
  archived_at: z.string().nullable().optional(),
});

export type Account = z.infer<typeof accountOutSchema>;

/** Whether the account is hidden from the account pool (`archived_at` set). */
export function isArchivedAccount(account: Pick<Account, 'archived_at'>): boolean {
  return account.archived_at !== null && account.archived_at !== undefined;
}

/**
 * The accounts that belong in the account POOL — every consumer that lists
 * or counts accounts (sidebar rows, the QR / ban attention banners, the
 * attention toasts, the fleet banner, the conversation-column account filter,
 * the send-account picker) goes through this. Label / color lookups for
 * existing conversations keep using the FULL list: an archived account's old
 * conversations must still be named.
 */
export function visibleAccounts<T extends Pick<Account, 'archived_at'>>(
  accounts: readonly T[],
): T[] {
  return accounts.filter((account) => !isArchivedAccount(account));
}

/** The hidden accounts — the admin's 「已隐藏 (N)」 section. */
export function archivedAccounts<T extends Pick<Account, 'archived_at'>>(
  accounts: readonly T[],
): T[] {
  return accounts.filter(isArchivedAccount);
}

/**
 * Label shown for an account that has never paired — there is no phone
 * number to display yet (the auto-generated epoch id is meaningless to an
 * operator and must never surface).
 */
export const UNPAIRED_ACCOUNT_LABEL = '待配对';

/**
 * The label text the console shows for an account. Centralized here as the
 * SINGLE entry point for account display — the sidebar row (`AccountRow`),
 * the conversation-column filter (`AccountFilterMenu`), the aggregate-view
 * badges (`ConversationList` / `ConversationRow`), the merged-thread member
 * dots + send-account picker (`MergedConversationRow` / `ThreadPanel`) and
 * the QR dialogs / toasts all go through it.
 *
 * Since the add-account flow stopped asking operators to type an id
 * (2026-08-05 `wa__` ghost-account incident), accounts are NAMED BY THE
 * PAIRED PHONE'S NUMBER:
 *
 *   - `phone_e164` known: Malaysian numbers render in the local roster
 *     convention (`+60165552121` → `0165552121`); any other country code
 *     renders as the bare digits without the `+` (`+8613800138000` →
 *     `8613800138000`).
 *   - `phone_e164` unknown (legacy rows / mid-pairing): fall back to the
 *     first run of consecutive digits in `external_account_id`
 *     (`wa_0136139981` → `0136139981`; the `wa__0136139981` ghost form
 *     resolves to the same digits). The digits only count as a phone when
 *     they look like the existing roster convention — leading `0`, at least
 *     9 digits. Anything else (auto-generated epoch ids, no digits at all)
 *     shows {@link UNPAIRED_ACCOUNT_LABEL}.
 */
export function formatAccountLabel(account: Account): string {
  const phone = account.phone_e164;
  if (phone !== null && phone.startsWith('+') && phone.length > 1) {
    if (phone.startsWith('+60')) return `0${phone.slice(3)}`;
    return phone.slice(1);
  }
  const digits = /[0-9]+/.exec(account.external_account_id)?.[0];
  if (digits !== undefined && digits.startsWith('0') && digits.length >= 9) {
    return digits;
  }
  return UNPAIRED_ACCOUNT_LABEL;
}

/** `GET /api/v1/accounts` — full unpaged list, ordered by `created_at`. */
export async function getAccounts(): Promise<Account[]> {
  const response = await apiClient.get('/accounts');
  return z.array(accountOutSchema).parse(response.data);
}

/**
 * Mirror of the Hub's `AccountQrOut` pydantic model (see
 * `hub/app/api/schemas.py`, M6-T3).
 *
 *   - `image_base64` — base64 body of a PNG (no `data:` prefix); the dialog
 *     wraps it as `data:image/png;base64,...` for an `<img>`. May be `null`
 *     when the gateway returned a reconnect-success without a fresh QR.
 *   - `account_status` — live `accounts.status` echo so the dialog can
 *     compare against the cached row without a second roundtrip.
 *   - `message` — gateway-supplied human hint ("Scan this QR in
 *     WhatsApp -> Linked Devices.").
 *   - `expires_at` — currently always `null`; the gateway doesn't expose
 *     a per-QR TTL, so the dialog polls instead.
 */
const accountQrOutSchema = z.object({
  image_base64: z.string().nullable(),
  account_status: z.string(),
  message: z.string().nullable().optional().default(null),
  expires_at: z.string().datetime({ offset: true }).nullable().optional().default(null),
});

export type AccountQr = z.infer<typeof accountQrOutSchema>;

/**
 * `GET /api/v1/accounts/{id}/qr` — fetch the latest WhatsApp pairing QR for an
 * account in `qr_required` state.
 *
 * The Hub returns 409 for a non-`qr_required` account (we don't expose stale
 * QRs) and 404 for an unknown account; both surface as axios errors with the
 * status code on `error.response.status` — the dialog handles them by closing.
 */
export async function fetchAccountQr(accountId: string): Promise<AccountQr> {
  const response = await apiClient.get(`/accounts/${accountId}/qr`);
  return accountQrOutSchema.parse(response.data);
}

/**
 * `POST /api/v1/accounts` — admin-only self-serve account registration (M6,
 * reworked 2026-08: NO operator-chosen id).
 *
 * The Hub auto-generates the `external_account_id` (operators used to type it
 * — a `wa__<digits>` typo once paired a live phone under a ghost account) and
 * later renames/labels the account from the paired phone's number. Sends an
 * empty JSON body. Creates the row at `status='qr_required'` and hot-reloads
 * the gateway config so the QR endpoint can pair it. Returns the new account
 * on 201; throws an AxiosError on 503 (gateway write failure — the operation
 * is safe to retry).
 */
export async function addAccount(): Promise<Account> {
  const response = await apiClient.post('/accounts', {});
  return accountOutSchema.parse(response.data);
}

/**
 * `POST /api/v1/accounts/{id}/rescan` — admin-only re-scan (repair) of a
 * **banned** account.
 *
 * The human-initiated counterpart of the automatic ban-stem: the Hub wipes the
 * account's now-dead credentials (`channels.logout`), re-enables it in the
 * gateway config + supervisor (`channels.start`), and flips the row to
 * `qr_required` — after which the EXISTING QR endpoint + `QrRequiredDialog`
 * drive a fresh pairing exactly as for a newly-added account.
 *
 * Returns the updated account (status `qr_required`) on 200; throws an
 * AxiosError on 404 (unknown account), 409 (the account is not `banned` — the
 * detail echoes the live status), or 503 (gateway / config unavailable — the
 * DB is left `banned`, so an exact retry is safe). No request body.
 */
export async function rescanAccount(accountId: string): Promise<Account> {
  const response = await apiClient.post(`/accounts/${accountId}/rescan`);
  return accountOutSchema.parse(response.data);
}

/**
 * `POST /api/v1/accounts/{id}/reconnect` — admin-only surgical reconnect of a
 * single account WITHOUT restarting the gateway.
 *
 * The non-destructive counterpart of {@link rescanAccount}: it finalizes a
 * Baileys 515 ("restart required after pairing") or cleanly re-establishes a
 * stuck account's runtime by cycling ONLY that account (`channels.stop` then
 * `channels.start`), leaving every other account — and the gateway itself —
 * untouched. Credentials are PRESERVED (no `channels.logout`), so there is no
 * re-pairing: the account reconnects with its existing WhatsApp login.
 *
 * On a `banned` row it is the retry WITH preserved credentials (the console's
 * 重试连接（保留凭据）, behind `BannedRetryDialog`): the hub writes
 * `enabled:true` back, re-arms its ban detection and cycles the channel; the
 * row stays `banned` until the gateway reports a real `connected`, and a
 * number WhatsApp still refuses is stemmed again after two polls. A pre-1.1
 * hub answers that request 409 (`banned` — its detail points at re-scan).
 *
 * Returns the CURRENT account row (200); the `status` is deliberately NOT
 * forced by the Hub (it may still read `qr_required` / `connecting` /
 * `banned` — the real `connected` state lands via the status poll). Throws an
 * AxiosError on 404 (unknown account) or 503 (gateway / adapter / config
 * unavailable — the DB row is left unchanged, so an exact retry is safe). No
 * request body.
 */
export async function reconnectAccount(accountId: string): Promise<Account> {
  const response = await apiClient.post(`/accounts/${accountId}/reconnect`);
  return accountOutSchema.parse(response.data);
}

/**
 * `POST /api/v1/accounts/{id}/archive` — admin-only: hide a dead / duplicate
 * account from the account pool.
 *
 * DB-ONLY on the hub: no gateway call (no logout / stop / start), no gateway
 * config write, no credential touched — history (conversations, messages) is
 * kept and {@link unarchiveAccount} restores it. Returns the row with
 * `archived_at` set (200; idempotent). Throws an AxiosError on 404 (unknown
 * account) or 409 (the account is `connected` — a live number is never
 * hidden). No request body.
 */
export async function archiveAccount(accountId: string): Promise<Account> {
  const response = await apiClient.post(`/accounts/${accountId}/archive`);
  return accountOutSchema.parse(response.data);
}

/**
 * `POST /api/v1/accounts/{id}/unarchive` — admin-only: show a hidden account
 * in the pool again (DB-only, like {@link archiveAccount}). Returns the row
 * with `archived_at: null` (200; idempotent). Throws an AxiosError on 404.
 */
export async function unarchiveAccount(accountId: string): Promise<Account> {
  const response = await apiClient.post(`/accounts/${accountId}/unarchive`);
  return accountOutSchema.parse(response.data);
}
